import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { provisioningApi, ProvisionOrganizationRequest } from '../services/provisioningApi'
import { useInstance } from '../contexts/InstanceContext'
import { useNavigate } from 'react-router-dom'

const steps = ['Infrastructure', 'Database', 'Registration']

export default function AddOrganization() {
  const [formData, setFormData] = useState<ProvisionOrganizationRequest>({
    organization_name: '',
    organization_domain: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
  })
  const [provisioning, setProvisioning] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const { refreshInstances } = useInstance()
  const navigate = useNavigate()

  useEffect(() => {
    if (jobId && provisioning) {
      const interval = setInterval(async () => {
        try {
          const status = await provisioningApi.getProvisioningStatus(jobId)
          
          // Update step based on status
          if (status.step === 'infrastructure') {
            setActiveStep(0)
          } else if (status.step === 'database') {
            setActiveStep(1)
          } else if (status.step === 'registration') {
            setActiveStep(2)
          }

          if (status.status === 'ready') {
            clearInterval(interval)
            setProvisioning(false)
            setStatus('Organization provisioned successfully!')
            setActiveStep(3)
            // Refresh instances list
            await refreshInstances()
            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)
          } else if (status.status === 'failed') {
            clearInterval(interval)
            setProvisioning(false)
            setError(status.error || 'Provisioning failed')
            setStatus(null)
          } else {
            setStatus(`Provisioning... ${status.step || 'in progress'}`)
          }
        } catch (err: any) {
          console.error('Error checking status:', err)
          setError(err.message || 'Error checking provisioning status')
        }
      }, 3000) // Check every 3 seconds

      return () => clearInterval(interval)
    }
  }, [jobId, provisioning, refreshInstances, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setProvisioning(true)
    setStatus(null)
    setError(null)
    setActiveStep(0)

    try {
      const response = await provisioningApi.provisionOrganization(formData)
      setJobId(response.job_id)
      setStatus(`Provisioning started. Job ID: ${response.job_id}`)
    } catch (err: any) {
      setProvisioning(false)
      setError(err.response?.data?.detail || err.message || 'Failed to start provisioning')
      setStatus(null)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Add New Organization</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      {provisioning && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Provisioning Status
          </Typography>
          <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <LinearProgress sx={{ mb: 2 }} />
          {status && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {status}
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Organization Details
          </Typography>
          <TextField
            fullWidth
            label="Organization Name"
            value={formData.organization_name}
            onChange={(e) =>
              setFormData({ ...formData, organization_name: e.target.value })
            }
            required
            margin="normal"
            disabled={provisioning}
          />
          <TextField
            fullWidth
            label="Domain"
            value={formData.organization_domain}
            onChange={(e) =>
              setFormData({ ...formData, organization_domain: e.target.value })
            }
            required
            margin="normal"
            helperText="e.g., org-name.karyva.com (will be used to generate instance ID)"
            disabled={provisioning}
          />

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Admin User
          </Typography>
          <TextField
            fullWidth
            label="Admin Name"
            value={formData.admin_name}
            onChange={(e) =>
              setFormData({ ...formData, admin_name: e.target.value })
            }
            required
            margin="normal"
            disabled={provisioning}
          />
          <TextField
            fullWidth
            label="Admin Email"
            type="email"
            value={formData.admin_email}
            onChange={(e) =>
              setFormData({ ...formData, admin_email: e.target.value })
            }
            required
            margin="normal"
            disabled={provisioning}
          />
          <TextField
            fullWidth
            label="Admin Password"
            type="password"
            value={formData.admin_password}
            onChange={(e) =>
              setFormData({ ...formData, admin_password: e.target.value })
            }
            required
            margin="normal"
            helperText="Minimum 8 characters"
            disabled={provisioning}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={provisioning}
              startIcon={provisioning ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {provisioning ? 'Provisioning...' : 'Provision Organization'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              disabled={provisioning}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  )
}

